import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  Container,
  Divider,
  TextField,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ImageIcon from '@mui/icons-material/Image';

interface ProductData {
  title: string;
  description: string;
  color: number;
  thumbnail?: {
    url?: string | null;
  } | null;
}

interface Variant {
  variantName: string;
  id: string;
}

function resolveThumbnailUrl(data: ProductData): string | null {
  const raw = data.thumbnail?.url;
  const s = typeof raw === 'string' ? raw.trim() : '';
  return s.length > 0 ? s : null;
}

function ATCComponent(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const [productData, setProductData] = useState<ProductData | null>(null);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<Map<string, number>>(new Map());
  const [productUrl, setProductUrl] = useState<string>('');

  useEffect(() => {
    const atcParam = searchParams.get('atc');
    if (atcParam) {
      try {
        // Decode base64 and parse JSON
        const decodedData = atob(atcParam);
        const parsedData: ProductData = JSON.parse(decodedData);
        setProductData(parsedData);

        // Parse description to extract product URL and variants
        const lines = parsedData.description.split('\n');
        if (lines.length > 0) {
          setProductUrl(lines[0]); // First line is the product URL

          // Parse variants from remaining lines
          const variantList: Variant[] = lines
            .slice(1)
            .map((line) => {
              const [variantName, id] = line.split(': ');
              return { variantName: variantName.trim(), id: id.trim() };
            })
            .filter((variant) => variant.variantName && variant.id);

          setVariants(variantList);
        }
      } catch (error: unknown) {
        if (error instanceof Error && typeof window !== 'undefined' && 'reportError' in window) {
          window.reportError(error);
        }
      }
    }
  }, [searchParams]);

  const handleVariantToggle = (variantId: string) => {
    const newSelected = new Map(selectedVariants);
    if (newSelected.has(variantId)) {
      newSelected.delete(variantId);
    } else {
      newSelected.set(variantId, 1);
    }
    setSelectedVariants(newSelected);
  };

  const handleQuantityChange = (variantId: string, quantity: number) => {
    const newSelected = new Map(selectedVariants);
    if (quantity > 0) {
      newSelected.set(variantId, quantity);
    } else {
      newSelected.delete(variantId);
    }
    setSelectedVariants(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedVariants.size === variants.length) {
      // Deselect all
      setSelectedVariants(new Map());
    } else {
      // Select all with quantity 1
      const newSelected = new Map();
      variants.forEach((v) => newSelected.set(v.id, 1));
      setSelectedVariants(newSelected);
    }
  };

  const handleContinue = () => {
    if (selectedVariants.size === 0) return;

    // Extract domain from product URL
    const url = new URL(productUrl);
    const domain = url.origin;

    // Create Shopify ATC link with quantities
    const variantParams = Array.from(selectedVariants.entries())
      .map(([id, quantity]) => `${id}:${quantity}`)
      .join(',');
    const atcUrl = `${domain}/cart/${variantParams}`;

    // Redirect to Shopify ATC link
    window.location.href = atcUrl;
  };

  if (!productData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6" color="error">
          Datos ATC inválidos o faltantes
        </Typography>
      </Container>
    );
  }

  const thumbnailUrl = resolveThumbnailUrl(productData);

  return (
    <Container maxWidth="sm" sx={{ mt: 4, maxWidth: '700px !important' }}>
      <Card sx={{ mb: 3 }}>
        {thumbnailUrl ? (
          <CardMedia
            component="img"
            height="300"
            image={thumbnailUrl}
            alt={productData.title}
            sx={{ objectFit: 'cover' }}
          />
        ) : (
          <Box
            role="img"
            aria-label="Sin imagen del producto"
            sx={{
              height: 300,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <ImageIcon sx={{ fontSize: 64, color: 'action.disabled' }} aria-hidden />
          </Box>
        )}
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {productData.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <a href={productUrl} target="_blank" rel="noopener noreferrer">
              {productUrl}
            </a>
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Seleccionar Tallas
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={selectedVariants.size === variants.length && variants.length > 0}
                indeterminate={selectedVariants.size > 0 && selectedVariants.size < variants.length}
                onChange={handleSelectAll}
              />
            }
            label="Seleccionar Todo"
            sx={{ mb: 2 }}
          />

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {variants.map((variant) => {
              const isSelected = selectedVariants.has(variant.id);
              const quantity = selectedVariants.get(variant.id) || 0;

              return (
                <Box key={variant.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleVariantToggle(variant.id)}
                      />
                    }
                    label={variant.variantName}
                    sx={{ flexGrow: 1 }}
                  />

                  {isSelected && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(variant.id, quantity - 1)}
                        disabled={quantity <= 1}
                      >
                        <RemoveIcon />
                      </IconButton>

                      <TextField
                        type="number"
                        value={quantity}
                        onChange={(e) => {
                          const newQuantity = parseInt(e.target.value, 10) || 0;
                          handleQuantityChange(variant.id, newQuantity);
                        }}
                        inputProps={{ min: 1, style: { textAlign: 'center' } }}
                        sx={{ width: '60px' }}
                        size="small"
                      />

                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(variant.id, quantity + 1)}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={selectedVariants.size === 0}
              onClick={handleContinue}
            >
              Continuar al Carrito ({selectedVariants.size} artículos)
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

export default ATCComponent;
